import React, { useEffect, useState } from 'react';

import TpDialog from '../shared/tp_dialog';
import DataTable from '../shared/data_table';
import LoadFailed from '../shared/load_failed';
import StatusPill from '../shared/status_pill';
import CodeBreaks from '../shared/code_breaks';
import { Note, StatusCell, What, Who } from '../shared/cells';
import { fetch_rule_history } from './api';
import {
  refusal,
  version_changed_text,
  version_row,
  version_status_view,
  version_title
} from './logic';
import DownloadCsvButton from '../../../util_components/csv_export/download_csv_button';
import { HISTORY_CSV_COLUMNS, rules_csv_name } from './csv';
import './rules_engine.css';

const version_chip = (r) => {
  const st = version_status_view(r.status);
  return <StatusPill tone={st.tone}>{st.label}</StatusPill>;
};
const version_by = (r) => [<span>{r.changed_by}</span>, r.changed_at || null];

// Three tracks (list-row pattern): version and date · what it does · status and who; the note
// runs under the row. Cards below 600px.
const COLUMNS = [
  {
    key: 'version_no',
    role: 'identifier',
    label: 'Version',
    track: { compact: '120px', full: '132px' },
    render: (r) => <Who name={`v${r.version_no}`} meta={[`From ${r.effective_text}`]} />
  },
  {
    key: 'sentence',
    label: 'What it does',
    render: (r) => (
      <What
        title={
          <span className="tp-rx-sentence-strong">
            <CodeBreaks text={r.sentence} />
          </span>
        }
        lines={[
          r.teacher_title ? (
            <>
              Teachers read <b className="tp-rx-tt">{r.teacher_title}</b>
              {r.teacher_why ? ` · ${r.teacher_why}` : ''}
            </>
          ) : (
            'Not shown to teachers'
          ),
          `Changed: ${version_changed_text(r)}`
        ]}
      />
    )
  },
  {
    key: 'status',
    role: 'status',
    label: 'Status',
    track: { compact: '150px', full: '168px' },
    render: (r) => <StatusCell chip={version_chip(r)} meta={version_by(r)} />
  },
  {
    key: 'note',
    label: 'Note',
    line: { from: 'sentence', bare: true },
    render: (r) => {
      const text = r.is_seed ? 'Seeded from the old engine' : r.note;
      return text ? <Note label="Note" text={<span className="tp-rx-why">{text}</span>} /> : null;
    }
  }
];

const RuleHistoryDialog = ({ rule_key, highlight_version_id, on_close, on_error }) => {
  const [state, setState] = useState({ status: 'loading', data: null, error: '' });
  const [rev, setRev] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, status: 'loading', error: '' }));
    fetch_rule_history(rule_key)
      .then((data) => {
        if (!cancelled) setState({ status: 'ready', data, error: '' });
      })
      .catch((e) => {
        if (cancelled) return;
        const r = refusal(e, 'Could not load the rule history.');
        if (r.permission && on_error) on_error(e, r.text);
        setState({ status: 'error', data: null, error: r.text });
      });
    return () => {
      cancelled = true;
    };
  }, [rule_key, rev, on_error]);

  const versions = (state.data && Array.isArray(state.data.versions) && state.data.versions) || [];
  const rows = versions.map(version_row);
  const used = highlight_version_id
    ? versions.find((v) => v.version_id === highlight_version_id)
    : null;
  const name = rows.length ? (rows.find((r) => r.status === 'CURRENT') || rows[0]).name : '';

  return (
    <TpDialog title={name ? `Rule history: ${name}` : 'Rule history'} size="lg" on_close={on_close}>
      <div className="tp-modal tp-rx-hist">
        {state.status === 'loading' ? (
          <div className="tp-empty" role="status">
            Loading rule history…
          </div>
        ) : null}
        {state.status === 'error' ? (
          <LoadFailed text={state.error} on_retry={() => setRev((r) => r + 1)} />
        ) : null}
        {state.status === 'ready' && used ? (
          <div className="tp-banner tp-banner-info" role="note">
            This line was worked out by {version_title(used)}.
          </div>
        ) : null}
        {state.status === 'ready' && highlight_version_id && !used ? (
          <div className="tp-banner tp-banner-warn" role="note">
            The version this line names is not in this rule&apos;s history.
          </div>
        ) : null}
        {state.status === 'ready' ? (
          <p className="tp-sub tp-rx-hist-lead">
            Each class uses the version in force at its scheduled start (IST); month amounts use the
            version in force at the month&apos;s end. Versions are never edited or removed.
          </p>
        ) : null}
        {state.status === 'ready' && rows.length ? (
          <div className="tp-rx-csv">
            <DownloadCsvButton
              columns={HISTORY_CSV_COLUMNS}
              rows={rows}
              file_name={() => rules_csv_name(`history ${rule_key}`)}
            />
          </div>
        ) : null}
        {state.status === 'ready' ? (
          <DataTable
            columns={COLUMNS}
            rows={rows}
            rowKey={(r) => r.key}
            card_below={600}
            rowClassName={(r) => (r.version_id === highlight_version_id ? 'tp-row-hl' : '')}
            emptyText="This rule has no versions."
          />
        ) : null}
      </div>
    </TpDialog>
  );
};

export default RuleHistoryDialog;
